import React, { useState,useMemo } from 'react';
 
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { MapPin, ArrowDown, ArrowUp, Phone } from 'lucide-react';
import { SortDirection } from '@/hooks/useSearch';
import { useClassificationOptions } from '@/features/alojamientos/hooks/useClassificationOptions';
import { useLocalities } from '@/features/alojamientos/hooks/useLocalities';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { RoleBasedElement } from '@/features/auth/components/RoleBasedElement';
import { ROLES } from '@/features/auth/constants/roles';
import { useStatusOptions } from '@/features/alojamientos/hooks/useStatusOptions';


// Definir el tipo SortField
export type SortField = 'establishmentName' | 'fileNumber' | 'categoryId' | 'localityId' | 'phone' | 'statusId';

// Define the establishment type
export interface Establishment {
  id: string;
  establishmentName: string;
  categoryId: number;
  category: {
    id: number;
    name: string;
    typeId: number;
    type: {
      id: number;
      name: string;
    } | null;
  } | null;
  localityId: number;
  phone: string;
  statusId: number;
  fileNumber: string;
  whatsapp: string;

  email: string;
  website: string;
  facebook: string;
  instagram: string;
  youtube: string;
  street: string;
  streetNumber: string;
  neighborhood: string;
  managerName: string;
  ownerName: string;
  ownerEmail: string;
  cuit: string;
  isAccessible: boolean;
  hasWifi: boolean;
  hasBreakfast: boolean;
  hasRestaurant: boolean;
  hasCafeteria: boolean;
  hasGym: boolean;
  hasSpa: boolean;
  hasCasino: boolean;
  allowsPets: boolean;
  isTouristComplex: boolean;
  generalNotes: string;
  serviceNotes: string | null;
  geolocation: string;
  statusDate: string;
  enabledDate: string;
  lastInspectionDate: string | null;
  rateStartDate: string | null;
  rateEndDate: string | null;
  createdAt: string;
  updatedAt: string;

  observations: string;
  assignment?: string; // Optional field for the sample assignment
  estado: string; // New field for estado
  estadoObservado: 'activo' | 'cerrado' | 'baja definitiva'; // New field for estado observado with specific values
  nuevoEstado?: 'cerrado por refacción' | 'cerrado temporalmente' | 'cerrado por trámite de baja' | 'cerrado cambio de titularidad' | 'clausurado' | 'actualización de documentación';

}

interface ResultsTableProps {
  establishments: Establishment[];
  sortField: SortField;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
}

interface SortableHeaderProps {
  field: SortField;
  currentSortField: SortField;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
  children: React.ReactNode;
}

const SortableHeader: React.FC<SortableHeaderProps> = ({ 
  field, 
  currentSortField, 
  sortDirection, 
  onSort, 
  children 
}) => {
  const isSorted = currentSortField === field;
  
  return (
    <TableHead className="font-medium cursor-pointer" onClick={() => onSort(field)}>
      <div className="flex items-center gap-1">
        {children}
        {isSorted && (
          <span className="ml-1">
            {sortDirection === 'asc' ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
          </span>
        )}
      </div>
    </TableHead>
  );
};

const ResultsTable: React.FC<ResultsTableProps> = ({ 
  establishments, 
  sortField, 
  sortDirection, 
  onSort 
}) => {
  const { categoryOptions } = useClassificationOptions(null);
  const { localities } = useLocalities(null);

  // State for assignment selection
  const [assignments, setAssignments] = useState<{[key: string]: string}>({});
  // State for estado observado
  const [estadosObservados, setEstadosObservados] = useState<{[key: number]: 'activo' | 'cerrado' | 'baja definitiva'}>({});
  // State for nuevo estado
  const [nuevosEstados, setNuevosEstados] = useState<{[key: number]: 'cerrado por refacción' | 'cerrado temporalmente' | 'cerrado por trámite de baja' | 'cerrado cambio de titularidad' | 'clausurado' | 'actualización de documentación'}>({});

  // Handle the assignment change
  const handleAssignmentChange = (establishmentId: string, value: string) => {
    setAssignments(prev => ({
      ...prev,
      [establishmentId]: value
    }));
  };

  // Handle estado observado change
  const handleEstadoObservadoChange = (establishmentId: number, value: 'activo' | 'cerrado' | 'baja definitiva') => {
    setEstadosObservados(prev => ({
      ...prev,
      [establishmentId]: value
    }));
    // Reset nuevo estado if not cerrado
    if (value !== 'cerrado') {
      setNuevosEstados(prev => {
        const newState = { ...prev };
        delete newState[establishmentId];
        return newState;
      });
    }
  };

  // Handle nuevo estado change
  const handleNuevoEstadoChange = (establishmentId: number, value: 'cerrado por refacción' | 'cerrado temporalmente' | 'cerrado por trámite de baja' | 'cerrado cambio de titularidad' | 'clausurado' | 'actualización de documentación') => {
    setNuevosEstados(prev => ({
      ...prev,
      [establishmentId]: value
    }));
  };

  const getCategoryName = (establishment: Establishment) => {
    const typeName = establishment.category?.type?.name;
    const categoryName = establishment.category?.name;
    
    if (!typeName && !categoryName) {
      return '-';
    }
    
    if (typeName && categoryName) {
      return `${typeName} - ${categoryName}`;
    }
    
    return typeName || categoryName || '-';
  };

  // Función para obtener el nombre de la localidad
  const getLocalityName = (localityId: number) => {
    const locality = localities?.find(loc => loc.id === localityId);
    return locality?.name || '-';
  };

  const { statusOptions, allSubstatusOptions } = useStatusOptions(null);

  const useTransformedOptions = (options: any[]) => {
    return useMemo(() => 
      options.map((t) => ({
        value: t.id,
        label: t.name,
        id: t.id.toString()
      })),
      [options]
    );
  };

  const statusOptionsL = useTransformedOptions(statusOptions);

  // Función para obtener el estado
  const getStatusName = (statusId: number) => {
    const status = statusOptionsL.find(option => option.value === statusId);
    return status?.label || '-';
  };

  return (
    <div className="overflow-x-auto">
      <Table className="border border-gray-200">
        <TableHeader className="bg-gray-100">
          <TableRow>
            <SortableHeader field="fileNumber" currentSortField={sortField} sortDirection={sortDirection} onSort={onSort}>
              Legajo
            </SortableHeader>
            <SortableHeader field="establishmentName" currentSortField={sortField} sortDirection={sortDirection} onSort={onSort}>
              Establecimiento
            </SortableHeader>
            <SortableHeader field="categoryId" currentSortField={sortField} sortDirection={sortDirection} onSort={onSort}>
              Categoría
            </SortableHeader>
            <SortableHeader field="localityId" currentSortField={sortField} sortDirection={sortDirection} onSort={onSort}>
              Localidad
            </SortableHeader>
            <TableHead className="font-medium">Dirección</TableHead>
            <SortableHeader field="phone" currentSortField={sortField} sortDirection={sortDirection} onSort={onSort}>
              Teléfono
            </SortableHeader>
            <TableHead className="font-medium">WhatsApp</TableHead>

            <TableHead className="font-medium">Email</TableHead>
            <SortableHeader field="statusId" currentSortField={sortField} sortDirection={sortDirection} onSort={onSort}>
              Estado
            </SortableHeader>
            <TableHead className="font-medium">Observaciones</TableHead>
            <TableHead className="font-medium">Acciones</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {establishments.map((establishment) => (
            <TableRow 
              key={establishment.id} 
              className="cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => window.location.href = `/accommodation/${establishment.id}`}
            >
              <TableCell>
                {establishment.fileNumber}
              </TableCell>
              <TableCell className="font-medium">
                <div>
                  {establishment.establishmentName}
                </div>
              </TableCell>
              <TableCell>
                <div>
                  {getCategoryName(establishment)}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1">
                  <MapPin size={16} className="text-gray-400" />
                  {getLocalityName(establishment.localityId)}
                </div>
              </TableCell>
              <TableCell>
                {[
                  establishment.street,
                  establishment.streetNumber,
                  establishment.neighborhood
                ].filter(Boolean).join(', ')}
              </TableCell>
              <TableCell>{establishment.phone}</TableCell>
              <TableCell>
                {establishment.whatsapp ? (
                  <div className="flex items-center gap-1">
                    <Phone size={16} className="text-green-500" />
                    {establishment.whatsapp}
                  </div>
                ) : '-'}
              </TableCell>
              <TableCell>{establishment.email}</TableCell>
              <TableCell>
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  {getStatusName(establishment.statusId)}
                </Badge>
              </TableCell>
              <TableCell>{establishment.observations}</TableCell>
              <TableCell>
                <Button 
                  variant="secondary" 
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.location.href = `/accommodation/${establishment.id}`;
                  }}
                >
                  Ver Detalles
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default ResultsTable;
